
import { ApiClient } from "./client";
import { supabase } from "@/integrations/supabase/client";

class ChannelsApi extends ApiClient {
  /**
   * Fetch all channels
   */
  async getAllChannels() {
    return this.executeQuery(
      async () => await supabase
        .from('channels')
        .select('*')
        .order('name'),
      "Failed to fetch channels"
    );
  }

  /**
   * Fetch a channel by ID
   */
  async getChannelById(id: string) {
    return this.executeQuery(
      async () => await supabase
        .from('channels')
        .select('*')
        .eq('id', id)
        .maybeSingle(),
      `Failed to fetch channel with ID ${id}`
    );
  }

  /**
   * Update a channel
   */
  async updateChannel(
    channelId: string,
    updateData: Partial<{
      name: string;
      description: string;
      logo_light: string;
      logo_dark: string;
      thumbnail: string;
    }>
  ) {
    try {
      // First try using the RPC function
      const { data: rpcResponse, error: rpcError } = await supabase.rpc('admin_update_channel', {
        p_channel_id: channelId,
        p_data: updateData
      });

      if (!rpcError) {
        return {
          success: true,
          message: 'Channel updated successfully',
          data: rpcResponse
        };
      }

      // Fallback to direct update if RPC fails
      const { data: directData, error: directError } = await supabase
        .from('channels')
        .update(updateData)
        .eq('id', channelId)
        .select();
      
      if (directError) {
        return {
          success: false,
          message: `Failed to update channel: ${directError.message}`,
        };
      }
      
      return {
        success: true,
        message: 'Channel updated successfully using fallback method',
        data: directData
      };
    } catch (error: any) {
      return {
        success: false,
        message: `An unexpected error occurred: ${error.message}`,
      };
    }
  }

  /**
   * Increment channel view count
   */
  async incrementChannelView(channelId: string) {
    return this.executeQuery(
      async () => await supabase.rpc('increment_channel_views_secure', { channel_id: channelId }),
      `Failed to increment view count for channel ${channelId}`
    );
  }
}

export const channelsApi = new ChannelsApi();
